import { Link } from 'react-router-dom'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { formatPrecise } from '@/lib/format'

interface LeaderboardEntry {
  id: string
  name: string
  image_url: string | null
  count: number
}

interface AvatarLeaderboardProps {
  entries: LeaderboardEntry[]
  valueLabel: string
  size?: 'default' | 'lg'
  linkTo?: (entry: LeaderboardEntry) => string
}

function initials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export function AvatarLeaderboard({ entries, valueLabel, size = 'default', linkTo }: AvatarLeaderboardProps) {
  const max = Math.max(...entries.map((e) => e.count), 1)
  const avatarSize = size === 'lg' ? 'size-11' : 'size-8'
  const nameSize = size === 'lg' ? 'text-base' : 'text-sm'

  return (
    <ul className="flex flex-col gap-3">
      {entries.map((entry, index) => {
        const row = (
          <>
            <span className="w-4 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
              {index + 1}
            </span>
            <Avatar className={`${avatarSize} shrink-0 ring-1 ring-border`}>
              <AvatarImage src={entry.image_url ?? undefined} alt="" />
              <AvatarFallback className="text-xs">{initials(entry.name)}</AvatarFallback>
            </Avatar>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className={`truncate font-medium ${nameSize}`}>{entry.name}</span>
                <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
                  {formatPrecise(entry.count)} {valueLabel}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${(entry.count / max) * 100}%` }}
                />
              </div>
            </div>
          </>
        )

        return (
          <li key={entry.id}>
            {linkTo ? (
              <Link to={linkTo(entry)} className="flex items-center gap-3 rounded-lg -mx-2 px-2 py-1 hover:bg-muted">
                {row}
              </Link>
            ) : (
              <div className="flex items-center gap-3">{row}</div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
